// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useCallback, useState, useSyncExternalStore } from "react";
import { DATE_FORMAT_OPTIONS, type DateFormatPreference, DEFAULT_DATE_FORMAT } from "@/lib/format";

/**
 * Device-local user preferences persisted in localStorage. These are NOT synced
 * to the backend — they're surfaced and managed from the profile Settings tab.
 *
 * Theme (`theme`, via next-themes) and language (`i18nextLng`, via i18next) have
 * their own storage managed by those libraries; this module owns the rest.
 */

const ITEMS_PER_PAGE_KEY = "pref:itemsPerPage";
const HTTP_ERROR_TOASTS_KEY = "pref:httpErrorToasts";
const DATE_FORMAT_KEY = "pref:dateFormat";
const ACCENT_KEY = "pref:accent";

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 20;

/** Default number of table rows per page, read live from localStorage. */
export function getDefaultPageSize(): number {
  if (typeof localStorage === "undefined") return DEFAULT_PAGE_SIZE;
  const raw = Number(localStorage.getItem(ITEMS_PER_PAGE_KEY));
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(raw) ? raw : DEFAULT_PAGE_SIZE;
}

export function setDefaultPageSize(size: number): void {
  localStorage.setItem(ITEMS_PER_PAGE_KEY, String(size));
}

/**
 * Whether to surface raw HTTP error toasts (422 / 5xx / network failures) globally.
 * Off by default so end users never see the developer-oriented JSON dump; QA can flip
 * it on from the profile Settings tab to inspect failing requests on any build. Read
 * live from localStorage so the non-React API client can check it per response.
 */
export function getShowHttpErrorToasts(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(HTTP_ERROR_TOASTS_KEY) === "true";
}

export function setShowHttpErrorToasts(enabled: boolean): void {
  localStorage.setItem(HTTP_ERROR_TOASTS_KEY, String(enabled));
}

/**
 * How dates are ordered on screen — see the rationale in `@/lib/format`. Defaults to
 * `system` so the viewer's own regional settings win; the profile picker exists for
 * people whose OS setting doesn't match how they read dates.
 *
 * Unlike the other preferences this one needs to be *reactive*: dates render in dozens
 * of components, so flipping the picker has to repaint them without a reload. The
 * listener set below is a minimal external store for `useSyncExternalStore`.
 */
const dateFormatListeners = new Set<() => void>();

export function getDateFormat(): DateFormatPreference {
  if (typeof localStorage === "undefined") return DEFAULT_DATE_FORMAT;
  const raw = localStorage.getItem(DATE_FORMAT_KEY);
  return DATE_FORMAT_OPTIONS.includes(raw as DateFormatPreference)
    ? (raw as DateFormatPreference)
    : DEFAULT_DATE_FORMAT;
}

export function setDateFormat(preference: DateFormatPreference): void {
  localStorage.setItem(DATE_FORMAT_KEY, preference);
  for (const listener of dateFormatListeners) listener();
}

function subscribeDateFormat(listener: () => void): () => void {
  dateFormatListeners.add(listener);
  return function unsubscribeDateFormat() {
    dateFormatListeners.delete(listener);
  };
}

/**
 * Reactive accessor for the date-format preference. Every date-rendering component
 * subscribes through this, so a change in the profile picker repaints all of them.
 */
export function useDateFormat(): [DateFormatPreference, (preference: DateFormatPreference) => void] {
  const value = useSyncExternalStore(subscribeDateFormat, getDateFormat, () => DEFAULT_DATE_FORMAT);
  const update = useCallback(function updateDateFormat(preference: DateFormatPreference) {
    setDateFormat(preference);
  }, []);
  return [value, update];
}

/**
 * Brand accent — a second theming axis, independent of light/dark. The palettes live in
 * `@/styles/globals.css` under `[data-accent="…"]`; this module only owns which one is
 * active on this device.
 *
 * It exists as a *safety* affordance: production and staging look identical, so people
 * fat-finger destructive changes on the wrong tab. Painting one environment red makes the
 * mistake visible before the click. Deliberately per-device and NOT env-derived — the
 * same build is served everywhere, and you sometimes want a neutral-looking production
 * window (customer screenshots, demos).
 *
 * Applied by stamping `data-accent` on <html>; `applyAccent` runs from `main.tsx` before
 * React mounts so there's no green flash on load. Because the switch is pure CSS custom
 * properties, changing it repaints without re-rendering the tree — the listener set below
 * exists only to keep the picker's own state in sync across mounts.
 */
export const ACCENT_OPTIONS = ["green", "red", "blue"] as const;
export type AccentPreference = (typeof ACCENT_OPTIONS)[number];
export const DEFAULT_ACCENT: AccentPreference = "green";

const accentListeners = new Set<() => void>();

export function getAccent(): AccentPreference {
  if (typeof localStorage === "undefined") return DEFAULT_ACCENT;
  const raw = localStorage.getItem(ACCENT_KEY);
  return ACCENT_OPTIONS.includes(raw as AccentPreference) ? (raw as AccentPreference) : DEFAULT_ACCENT;
}

/** Stamp the accent onto <html> so the CSS overrides take effect. */
export function applyAccent(accent: AccentPreference = getAccent()): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.accent = accent;
}

export function setAccent(accent: AccentPreference): void {
  localStorage.setItem(ACCENT_KEY, accent);
  applyAccent(accent);
  for (const listener of accentListeners) listener();
}

function subscribeAccent(listener: () => void): () => void {
  accentListeners.add(listener);
  return function unsubscribeAccent() {
    accentListeners.delete(listener);
  };
}

/** Reactive accessor for the accent preference. */
export function useAccent(): [AccentPreference, (accent: AccentPreference) => void] {
  const value = useSyncExternalStore(subscribeAccent, getAccent, () => DEFAULT_ACCENT);
  const update = useCallback(function updateAccent(accent: AccentPreference) {
    setAccent(accent);
  }, []);
  return [value, update];
}

/**
 * Clear all device-local UI preferences back to defaults. Intentionally does NOT
 * touch `access_token` (auth session) — only preferences. The caller should reload
 * so theme/language/i18n re-initialise from their defaults.
 */
export function resetLocalPreferences(): void {
  const exactKeys = ["theme", "i18nextLng", ITEMS_PER_PAGE_KEY, HTTP_ERROR_TOASTS_KEY, DATE_FORMAT_KEY, ACCENT_KEY];
  for (const key of exactKeys) localStorage.removeItem(key);

  // Per-table column visibility lives under `data-table:<key>:columns`.
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith("data-table:")) localStorage.removeItem(key);
  }

  // Sidebar open/collapsed state is session-scoped.
  sessionStorage.removeItem("sidebar_state");
}

/** Reactive accessor for the items-per-page preference. */
export function useItemsPerPage(): [number, (size: number) => void] {
  const [value, setValue] = useState(getDefaultPageSize);
  const update = useCallback(function updateItemsPerPage(size: number) {
    setDefaultPageSize(size);
    setValue(size);
  }, []);
  return [value, update];
}

/** Reactive accessor for the HTTP-error-toasts preference. */
export function useShowHttpErrorToasts(): [boolean, (enabled: boolean) => void] {
  const [value, setValue] = useState(getShowHttpErrorToasts);
  const update = useCallback(function updateShowHttpErrorToasts(enabled: boolean) {
    setShowHttpErrorToasts(enabled);
    setValue(enabled);
  }, []);
  return [value, update];
}
